import { z } from 'zod'
import { sqlEnumCheck } from './sql.ts'

/**
 * One definition per enum, serving every consumer from a single literal set.
 *
 * A domain enum has four consumers and they must never disagree:
 *
 * | Consumer | Uses |
 * |---|---|
 * | TypeScript | `Value` — the string-literal union |
 * | Runtime validation and the OpenAPI document | `schema` |
 * | `packages/db` | `sqlCheck(column)` — the `TEXT` + `CHECK` column |
 * | Application code | `values`, `is()` |
 *
 * `docs/ARCHITECTURE.md` §5 requires the CHECK constraint; deriving it from the same
 * tuple as the Zod schema is what makes "the union and the constraint cannot drift" a
 * property of the code rather than a promise in a review comment.
 */

/** A non-empty tuple of enum members. */
export type EnumValues = readonly [string, ...string[]]

/**
 * Builds a domain enum from its members, in declaration order.
 *
 * Declaration order is meaningful for enums that carry a hierarchy (`userRole`) or a
 * lifecycle (`exportJobStatus`), so it is preserved rather than sorted.
 *
 * @throws if a member is empty or repeated — both are typos that would otherwise produce
 * a schema that silently accepts less than it appears to, or a CHECK constraint listing
 * the same value twice.
 */
export function defineEnum<const T extends EnumValues>(name: string, values: T) {
  if (new Set(values).size !== values.length) {
    throw new Error(`Enum "${name}" has duplicate members: ${values.join(', ')}`)
  }
  if (values.some((value) => value.length === 0)) {
    throw new Error(`Enum "${name}" has an empty member.`)
  }

  const members: readonly string[] = values
  const schema = z.enum(values)

  return {
    /** The enum's name, used in error messages and as the OpenAPI schema name. */
    name,
    /** Every member, in declaration order. */
    values,
    /** Zod schema — runtime validation and the OpenAPI enum in one object. */
    schema,
    /** Narrowing type guard for values arriving from outside the type system. */
    is: (value: unknown): value is T[number] =>
      typeof value === 'string' && members.includes(value),
    /**
     * The `CHECK` constraint body for a `TEXT` column holding this enum.
     * `packages/db` wraps it: `CHECK (${sqlCheck('status')})`.
     */
    sqlCheck: (column: string): string => sqlEnumCheck(column, members),
  } as const
}

/** The string-literal union of a defined enum. */
export type EnumValueOf<E extends { readonly values: EnumValues }> = E['values'][number]

/** Convenience alias for the Zod output type of a defined enum. */
export type InferEnum<E extends { readonly schema: z.ZodType }> = z.infer<E['schema']>
